import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './agenda.module.scss';
import Calendar from './Calendar/Calendar';
import api from '../../../api/api';
import VisitView from './VisitView/VisitView';
import { useQuery } from '../../../utils/hooks/useQuery';
import { changeMenuPath } from '../../../store/actions/menu';
import Button from '../../_common/Form/Buttons/Button';
import VisitModal from '../../_common/Modal/VisitModal/VisitModal';
import Loader from '../../_common/Loader/Loader';
import NoteModal from '../../_common/Modal/NoteModal/NoteModal';
import accountHelpers from '../../../utils/accountHelpers';
import { deleteVisitRequest } from '../../../store/actions/physicians';
import AttentionModal from '../../_common/Modal/AttentionModal/AttentionModal';

const sortingList = (visitorsList, sortBy) => {
  let data = visitorsList;

  if (sortBy) {
    data = visitorsList.filter(({ Rep }) => Rep === sortBy);
  }

  const groupByDate = _.groupBy(data, (r) => moment.utc(r.dateTime).local().format('YYYY-MM-DD'));
  const list = _.map(groupByDate, (val, key) => ({ date: key, visits: val, repCount: _.uniq(val.map((v) => v.Rep)).length }));

  return _.orderBy(list, (l) => new Date(l.date));
};

const Agenda = () => {
  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();

  const dispatch = useDispatch();

  const firstLoad = useRef(true);
  const [rep, setRep] = useState('');
  const [date, setDate] = useState('');
  const [editInfo, setEditInfo] = useState({});
  const [list, setList] = useState([]);
  const [visitList, setVisitList] = useState([]);
  const [visitListIndex, setVisitListIndex] = useState(0);

  const [visitedUsers, setVisitedUsers] = useState([]);

  const [isScheduleModalOpen, scheduleModalToggle] = useState(false);

  const [isScheduleEdit, scheduleEditToggle] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [noteModalData, setNoteModalData] = useState({});
  const [note, setNote] = useState('');
  const [noteSaveLoading, setNoteSaveLoading] = useState(false);

  const getPhysicianVisitList = async () => {
    const { data: { response } } = await api.getPhysicianVisits({});
    const sortList = sortingList(response);

    setVisitList(sortList);
    setList(response);

    if (query.rep) {
      setCurrentRep(query.rep, response, moment(query.date).isValid() ? query.date : null);
    } else if (moment(query.date).isValid()) {
      setCurrentDate(query.date, sortList);
    }
  };

  useEffect(() => {
    (async () => {
      await getPhysicianVisitList();

      firstLoad.current = false;
    })();
  }, []);

  useEffect(() => {
    if (!firstLoad.current) {
      const newQuery = { date, rep };
      dispatch(changeMenuPath('Agenda', `${pathname}${queryToStringify(newQuery)}`));

      setQuery(newQuery);
    }
  }, [date, rep]);

  const setCurrentRep = (currentRep, data = list, currentDate) => {
    setRep(currentRep);

    const sortList = sortingList(data, currentRep);

    setVisitList(sortList);

    if (currentDate) {
      setCurrentDate(currentDate, sortList);
    } else {
      const lastIndex = _.findLastIndex(sortList);

      setDate(sortList[lastIndex]?.date || '');
      setVisitListIndex(lastIndex || 0);
      setVisitedUsers(sortList[lastIndex]?.visits || []);
    }
  };

  const setCurrentDate = (currentDate, data = visitList) => {
    setDate(currentDate);

    const findDateIndex = data.findIndex((l) => l.date === currentDate);

    setVisitedUsers(data[findDateIndex]?.visits || []);

    if (findDateIndex) setVisitListIndex(findDateIndex);
  };

  const changeVisitDate = (prev) => {
    const findDateIndex = visitList.findIndex((l) => l.date === date);
    const changeToIndex = prev ? findDateIndex - 1 : findDateIndex + 1;

    setVisitedUsers(visitList[changeToIndex]?.visits);
    setVisitListIndex(changeToIndex);
    setDate(visitList[changeToIndex]?.date);
  };

  const closeNoteModal = useCallback(() => {
    setNoteModalData({});
    setNote('');
  }, []);

  const changeNote = useCallback((value) => {
    setNote(value);
  }, []);

  const setAgendaInfo = useCallback((info) => {
    const agendaDate = moment.utc(info.dateTime).local().format('YYYY-MM-DD');
    const agendaTime = moment.utc(info.dateTime).local().format('hh:mm A');

    setEditInfo({
      ...info,
      date: agendaDate,
      time: agendaTime,
    });

    scheduleEditToggle(true);
  }, []);

  const deleteVisit = useCallback(async () => {
    setDeleteLoading(true);
    await dispatch(deleteVisitRequest(deletingId));

    await getPhysicianVisitList();

    setDeleteLoading(false);

    setDeletingId('');
  }, [deletingId]);

  const saveNote = useCallback(async () => {
    if (!note) {
      toast.error('Please fill Note field');
    } else {
      setNoteSaveLoading(true);
      const { visitId, physicianId } = noteModalData;

      const sendData = {
        visitId,
        physicianId,
        userId: accountHelpers.getUser().id,
        name: accountHelpers.getUser().name,
        note, // .replace(/#/g, '%23') Allows literal hashtags to be written in note
        userImage: accountHelpers.getUser().link,
      };

      await api.addPhysicianNote(sendData);

      await getPhysicianVisitList();
      closeNoteModal();
      setNoteSaveLoading(false);
    }
  }, [note, noteModalData]);

  const closeVisitModal = () => {
    scheduleEditToggle(false);
    scheduleModalToggle(false);
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('width');
    document.body.ontouchmove = () => true;
  };

  return (
    <div className={classes.agenda_wrapper}>
      {firstLoad.current
        ? <Loader />

        : (
          <>
            <Calendar
              date={date}
              onChange={setCurrentDate}
              visitList={visitList}
              currentRep={rep}
              setRep={setCurrentRep}
              dateFormat="YYYY-MM-DD"
            />

            {!!visitedUsers.length && (
              <VisitView
                currentDate={date}
                visitedUsers={visitedUsers}
                showPrevBtn={!!visitListIndex}
                showNextBtn={visitListIndex !== visitList.length - 1}
                changeVisitDate={changeVisitDate}
                setAgendaInfo={setAgendaInfo}
                setDeletingId={setDeletingId}
                openingNoteAddModal={setNoteModalData}
              />
            )}

            <Button
              title="Schedule new visit"
              className={classes.schedule_btn}
              addButton
              type="button"
              reverseColor
              onClick={() => scheduleModalToggle(true)}
            />

            {isScheduleModalOpen && (
              <VisitModal
                isOpenModal={isScheduleModalOpen}
                onClose={closeVisitModal}
                currentDate={date}
                requestAfterSave={() => getPhysicianVisitList()}
              />
            )}

            {isScheduleEdit && (
              <VisitModal
                isOpenModal={isScheduleEdit}
                onClose={closeVisitModal}
                currentDate={editInfo.date}
                currentTime={editInfo.time}
                physicianName={editInfo.Physician}
                physicianId={editInfo.PhysicianId}
                repName={editInfo.Rep}
                groupName={editInfo.group}
                id={editInfo.id}
                requestAfterSave={() => getPhysicianVisitList()}
              />
            )}

            <NoteModal
              isOpenModal={!_.isEmpty(noteModalData)}
              onClose={closeNoteModal}
              textValue={note}
              showCheckbox={false}
              onChangeText={changeNote}
              saveNote={saveNote}
              saveLoading={noteSaveLoading}
            />

            <AttentionModal
              onClose={() => setDeletingId('')}
              isOpen={!!deletingId}
              onSave={deleteVisit}
              title="Delete Visit?"
              text="Are you sure you want to delete this visit?"
              loading={deleteLoading}
            />
          </>
        )}
    </div>
  );
};

export default Agenda;
